/**
 * sqlite3mt local commit registration helpers
 *
 * Mechanical extraction from §96 §11.3.
 *
 * This module owns the Control Worker path that turns a physical WAL commit
 * into durable local journal state, pending DB deltas, workspace state, and
 * a published integrated frontier.
 */

import {
    DEFAULT_PAGE_SIZE,
    Sab4Scalar,
    sab4ScalarView,
} from './sab-layout'
import type {
    Deferred,
    DurableReconcileIntent,
    PendingBaseAdvance,
} from './bootstrap'
import {
    COMMIT_ACK_RC_FATAL,
    COMMIT_ACK_RC_OK,
    abortIfFatal,
    publishCommitAck,
    type CommitAckRc,
} from './control'
import {
    applyDeltaIntoWorkspaceOverlay,
    foldAllPendingIntoDbOverlay,
    insertPendingDeltaOrdered,
    noteObservedWalTipFromAppend,
} from './dbview'
import { publishFrontierStateToSabIfChanged } from './frontier'
import {
    sameWalSalt,
    type Delta,
    type FrameMax,
    type PageHash,
    type Pgno,
    type PublicHead,
    type WalSnapshot,
} from './types'

export const INTERNAL_COMMIT_REG_NEGATIVE = 'INTERNAL_COMMIT_REG_NEGATIVE'
export const INTERNAL_HEAD_ADVANCE_REENTRY = 'INTERNAL_HEAD_ADVANCE_REENTRY'
export const BASE_ADVANCE_FINALIZATION_FAILED = 'BASE_ADVANCE_FINALIZATION_FAILED'
export const LOCAL_COMMIT_LEFT_UNINTEGRATED = 'LOCAL_COMMIT_LEFT_UNINTEGRATED'
export const LOCAL_COMMIT_DURING_APPENDING = 'LOCAL_COMMIT_DURING_APPENDING'
export const LOCAL_COMMIT_AFTER_WRITE_GATE_CLOSED = 'LOCAL_COMMIT_AFTER_WRITE_GATE_CLOSED'
export const LOCAL_COMMIT_REG_FATAL = 'LOCAL_COMMIT_REG_FATAL'
export const POST_LOCAL_COMMIT_CHECKPOINT_REASON = 'post-local-commit'

export interface CommitRegistrationBarrierState {
    pendingBaseAdvance: PendingBaseAdvance | null
    inflightCommitRegistrations: number
    headAdvancePreparing: boolean
    headAdvancePrepareWaiter: Deferred<void> | null
    commitRegistrationDrainWaiter: Deferred<void> | null
}

export interface LocalCommitRegistrationState extends CommitRegistrationBarrierState {
    dbOverlay: Map<Pgno, PageHash>
    dbPageCount: number
    workspaceBaseHead: PublicHead
    workspaceOverlay: Map<Pgno, PageHash>
    workspacePageCount: number
    pendingDbDeltas: Map<FrameMax, Delta>
    currentWalSalt0: number | null
    currentWalSalt1: number | null
    integratedFrameMax: number
    lastWalSnapshot: WalSnapshot | null
    localCommitSeq: bigint
    stagedCas: Map<PageHash, Uint8Array>
    reconcileIntent: DurableReconcileIntent | null
}

export interface LocalCommitRegistrationRuntime {
    runtimeEpoch: bigint
    syncFatalError: unknown | null
}

export interface DurableLocalCommitRecord {
    commitSeq: bigint
    workspaceBaseRoot: PublicHead['root']
    workspaceBaseVersion: bigint
    workspaceBasePageCount: number
    walSalt0: number
    walSalt1: number
    frameMax: FrameMax
    delta: Delta
}

export interface LocalCommitDurableTransaction {
    putBlob(hash: PageHash, bytes: Uint8Array): void | Promise<void>
    appendLocalCommit(record: DurableLocalCommitRecord): void | Promise<void>
}

export interface LocalCommitDurableStore {
    transaction<T>(
        callback: (tx: LocalCommitDurableTransaction) => Promise<T> | T
    ): Promise<T>
}

export interface WalCommitSyncMessage {
    runtimeEpoch: bigint
    connId: number
    reqId: bigint
    admittedWriteGateSeq?: number
    admittedCheckpointGateSeq?: number
    walSalt0: number
    walSalt1: number
    frameMax: FrameMax
    delta: Delta
    blobs: Map<PageHash, Uint8Array>
}

export interface LocalCommitRegistrationHooks {
    durable: LocalCommitDurableStore
    enterFatalRuntime(error: unknown, reason: string): void
    requestPersistLocalSnapshot(): void
    maybeRequestCheckpoint(reason: string): void | Promise<unknown>
    maybeStartPushOrImport(): void
    yieldOneTaskTurnForPendingCommitMessages(): Promise<void>
    classifyFatalLocalCommitRegistrationError?(error: unknown): unknown
}

function hasWriteGateClosedAfterCommitAdmission(
    msg: Pick<WalCommitSyncMessage, 'admittedWriteGateSeq'>,
    sab4: SharedArrayBuffer,
): boolean {
    const scalar = sab4ScalarView(sab4)
    if (Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED) === 0) {
        return false
    }

    const admittedWriteGateSeq = msg.admittedWriteGateSeq
    // Gate closure is not retroactive: a commit admitted before a later
    // WRITE_GATE_WAITSEQ bump must be integrated instead of fatal-acked.
    return typeof admittedWriteGateSeq === 'number'
        && admittedWriteGateSeq < Atomics.load(scalar, Sab4Scalar.WRITE_GATE_WAITSEQ)
}

function isUnadmittedCommitAfterWriteGateClosed(
    msg: Pick<WalCommitSyncMessage, 'admittedWriteGateSeq'>,
    sab4: SharedArrayBuffer,
): boolean {
    const scalar = sab4ScalarView(sab4)
    return Atomics.load(scalar, Sab4Scalar.WRITE_GATE_CLOSED) !== 0
        && !hasWriteGateClosedAfterCommitAdmission(msg, sab4)
}

export interface OnWalCommitSyncArgs {
    msg: WalCommitSyncMessage
    runtime: LocalCommitRegistrationRuntime
    state: LocalCommitRegistrationState
    sab1: SharedArrayBuffer
    sab4: SharedArrayBuffer
    hooks: LocalCommitRegistrationHooks
    pageSize?: number
}

export function createDeferred<T = void>(): Deferred<T> {
    let resolve!: Deferred<T>['resolve']
    let reject!: Deferred<T>['reject']
    const promise = new Promise<T>((res, rej) => {
        resolve = res
        reject = rej
    })
    return { promise, resolve, reject }
}

export function signalCommitRegistrationDrainIfNeeded(
    state: CommitRegistrationBarrierState
): void {
    if (state.inflightCommitRegistrations === 0 && state.commitRegistrationDrainWaiter != null) {
        state.commitRegistrationDrainWaiter.resolve()
        state.commitRegistrationDrainWaiter = null
    }
}

export async function waitUntilHeadAdvancePathClear(
    state: CommitRegistrationBarrierState,
    yieldOneTaskTurnForPendingCommitMessages: () => Promise<void>
): Promise<void> {
    for (;;) {
        if (!state.headAdvancePreparing && state.pendingBaseAdvance == null) {
            return
        }

        if (state.pendingBaseAdvance != null) {
            await state.pendingBaseAdvance.promise.catch(() => {})
            continue
        }

        const waiter = state.headAdvancePrepareWaiter
        if (waiter != null) {
            await waiter.promise.catch(() => {})
        } else {
            await yieldOneTaskTurnForPendingCommitMessages()
        }
    }
}

export async function enterCommitRegistrationLease(
    state: CommitRegistrationBarrierState,
    yieldOneTaskTurnForPendingCommitMessages: () => Promise<void>
): Promise<void> {
    for (;;) {
        await waitUntilHeadAdvancePathClear(state, yieldOneTaskTurnForPendingCommitMessages)

        state.inflightCommitRegistrations += 1

        if (state.headAdvancePreparing || state.pendingBaseAdvance != null) {
            state.inflightCommitRegistrations -= 1
            signalCommitRegistrationDrainIfNeeded(state)
            continue
        }

        return
    }
}

export function leaveCommitRegistrationLease(
    state: CommitRegistrationBarrierState
): void {
    state.inflightCommitRegistrations -= 1
    if (state.inflightCommitRegistrations < 0) {
        throw new Error(INTERNAL_COMMIT_REG_NEGATIVE)
    }
    signalCommitRegistrationDrainIfNeeded(state)
}

export async function beginHeadAdvancePreparationAndDrain(
    state: CommitRegistrationBarrierState,
    runtime: { syncFatalError: unknown | null }
): Promise<void> {
    if (state.headAdvancePreparing || state.pendingBaseAdvance != null) {
        throw new Error(INTERNAL_HEAD_ADVANCE_REENTRY)
    }

    state.headAdvancePreparing = true
    state.headAdvancePrepareWaiter = createDeferred()

    while (state.inflightCommitRegistrations > 0) {
        if (state.commitRegistrationDrainWaiter == null) {
            state.commitRegistrationDrainWaiter = createDeferred()
        }
        await state.commitRegistrationDrainWaiter.promise
        abortIfFatal(runtime)
    }
}

export function endHeadAdvancePreparation(
    state: CommitRegistrationBarrierState
): void {
    state.headAdvancePreparing = false
    if (state.headAdvancePrepareWaiter != null) {
        state.headAdvancePrepareWaiter.resolve()
        state.headAdvancePrepareWaiter = null
    }
}

export async function flushPendingBaseAdvanceIfAny(
    state: CommitRegistrationBarrierState,
    enterFatalRuntime: (error: unknown, reason: string) => void
): Promise<void> {
    if (state.pendingBaseAdvance == null) {
        return
    }

    try {
        await state.pendingBaseAdvance.promise
    } catch (error) {
        enterFatalRuntime(BASE_ADVANCE_FINALIZATION_FAILED, BASE_ADVANCE_FINALIZATION_FAILED)
        throw error
    } finally {
        state.pendingBaseAdvance = null
    }
}

export async function onWalCommitSync({
    msg,
    runtime,
    state,
    sab1,
    sab4,
    hooks,
    pageSize = DEFAULT_PAGE_SIZE,
}: OnWalCommitSyncArgs): Promise<'IGNORED' | 'ACKED'> {
    const debugState = state as LocalCommitRegistrationState & {
        __debugLastCommitSync?: Record<string, unknown>
    }
    if (msg.runtimeEpoch !== runtime.runtimeEpoch) {
        debugState.__debugLastCommitSync = {
            outcome: 'ignored-runtime-mismatch',
            connId: msg.connId,
            reqId: msg.reqId.toString(),
            messageRuntimeEpoch: msg.runtimeEpoch.toString(),
            runtimeEpoch: runtime.runtimeEpoch.toString(),
        }
        return 'IGNORED'
    }

    let ackRc: CommitAckRc = COMMIT_ACK_RC_FATAL
    let needPostAckKick = false
    let leaseHeld = false

    try {
        if (runtime.syncFatalError != null) {
            return 'ACKED'
        }

        if (state.reconcileIntent != null && state.reconcileIntent.phase === 'APPENDING') {
            hooks.enterFatalRuntime(LOCAL_COMMIT_LEFT_UNINTEGRATED, LOCAL_COMMIT_DURING_APPENDING)
            return 'ACKED'
        }

        if (isUnadmittedCommitAfterWriteGateClosed(msg, sab4)) {
            if (runtime.syncFatalError == null) {
                hooks.enterFatalRuntime(LOCAL_COMMIT_LEFT_UNINTEGRATED, LOCAL_COMMIT_AFTER_WRITE_GATE_CLOSED)
            }
            return 'ACKED'
        }

        await enterCommitRegistrationLease(state, hooks.yieldOneTaskTurnForPendingCommitMessages)
        leaseHeld = true

        if (runtime.syncFatalError != null) {
            return 'ACKED'
        }

        if (state.reconcileIntent != null && state.reconcileIntent.phase === 'APPENDING') {
            hooks.enterFatalRuntime(LOCAL_COMMIT_LEFT_UNINTEGRATED, LOCAL_COMMIT_DURING_APPENDING)
            return 'ACKED'
        }

        if (isUnadmittedCommitAfterWriteGateClosed(msg, sab4)) {
            if (runtime.syncFatalError == null) {
                hooks.enterFatalRuntime(LOCAL_COMMIT_LEFT_UNINTEGRATED, LOCAL_COMMIT_AFTER_WRITE_GATE_CLOSED)
            }
            return 'ACKED'
        }

        await flushPendingBaseAdvanceIfAny(state, hooks.enterFatalRuntime)

        if (runtime.syncFatalError != null) {
            return 'ACKED'
        }

        const commitSeq = state.localCommitSeq + 1n
        debugState.__debugLastCommitSync = {
            outcome: 'start',
            connId: msg.connId,
            reqId: msg.reqId.toString(),
            frameMax: msg.frameMax,
            walSalt0: msg.walSalt0,
            walSalt1: msg.walSalt1,
            commitSeq: commitSeq.toString(),
            integratedFrameMaxBefore: state.integratedFrameMax,
            pendingDbDeltaCountBefore: state.pendingDbDeltas.size,
        }

        await hooks.durable.transaction(async (tx) => {
            for (const [hash, bytes] of msg.blobs) {
                await tx.putBlob(hash, bytes)
            }
            await tx.appendLocalCommit({
                commitSeq,
                workspaceBaseRoot: state.workspaceBaseHead.root,
                workspaceBaseVersion: state.workspaceBaseHead.version,
                workspaceBasePageCount: state.workspacePageCount,
                walSalt0: msg.walSalt0,
                walSalt1: msg.walSalt1,
                frameMax: msg.frameMax,
                delta: msg.delta,
            })
        })

        abortIfFatal(runtime)

        state.localCommitSeq = commitSeq

        for (const [hash, bytes] of msg.blobs) {
            state.stagedCas.set(hash, bytes)
        }

        if (state.currentWalSalt0 == null) {
            state.currentWalSalt0 = msg.walSalt0
            state.currentWalSalt1 = msg.walSalt1
            state.integratedFrameMax = 0
            publishFrontierFromState(sab4, state)
        } else if (!sameWalSalt(state.currentWalSalt0, state.currentWalSalt1, msg.walSalt0, msg.walSalt1)) {
            const dbPageCountRef = { value: state.dbPageCount }
            foldAllPendingIntoDbOverlay(
                sab1,
                state.pendingDbDeltas,
                state.dbOverlay,
                dbPageCountRef,
                pageSize
            )
            state.dbPageCount = dbPageCountRef.value
            state.currentWalSalt0 = msg.walSalt0
            state.currentWalSalt1 = msg.walSalt1
            state.integratedFrameMax = 0
            publishFrontierFromState(sab4, state)
        }

        noteObservedWalTipFromAppend(state, msg.walSalt0, msg.walSalt1, msg.frameMax)
        insertPendingDeltaOrdered(state.pendingDbDeltas, msg.frameMax, msg.delta)

        const workspacePageCountRef = { value: state.workspacePageCount }
        applyDeltaIntoWorkspaceOverlay(
            state.workspaceOverlay,
            workspacePageCountRef,
            msg.delta
        )
        state.workspacePageCount = workspacePageCountRef.value

        if (msg.frameMax > state.integratedFrameMax) {
            state.integratedFrameMax = msg.frameMax
            publishFrontierFromState(sab4, state)
        }

        debugState.__debugLastCommitSync = {
            outcome: 'ack-ok',
            connId: msg.connId,
            reqId: msg.reqId.toString(),
            frameMax: msg.frameMax,
            commitSeq: state.localCommitSeq.toString(),
            integratedFrameMaxAfter: state.integratedFrameMax,
            pendingDbDeltaCountAfter: state.pendingDbDeltas.size,
            workspaceOverlaySize: state.workspaceOverlay.size,
        }

        needPostAckKick = true
        ackRc = COMMIT_ACK_RC_OK
    } catch (error) {
        debugState.__debugLastCommitSync = {
            outcome: 'fatal',
            connId: msg.connId,
            reqId: msg.reqId.toString(),
            frameMax: msg.frameMax,
            error: error instanceof Error ? { name: error.name, message: error.message, stack: error.stack } : String(error),
        }
        hooks.enterFatalRuntime(
            hooks.classifyFatalLocalCommitRegistrationError?.(error) ?? error,
            LOCAL_COMMIT_REG_FATAL
        )
        ackRc = COMMIT_ACK_RC_FATAL
    } finally {
        if (leaseHeld) {
            leaveCommitRegistrationLease(state)
        }
        publishCommitAck(sab4, msg.connId, msg.reqId, ackRc)
    }

    if (needPostAckKick && runtime.syncFatalError == null) {
        hooks.requestPersistLocalSnapshot()
        await hooks.maybeRequestCheckpoint(POST_LOCAL_COMMIT_CHECKPOINT_REASON)
        hooks.maybeStartPushOrImport()
    }

    return 'ACKED'
}

function publishFrontierFromState(
    sab4: SharedArrayBuffer,
    state: Pick<LocalCommitRegistrationState, 'currentWalSalt0' | 'currentWalSalt1' | 'integratedFrameMax'>
): void {
    publishFrontierStateToSabIfChanged(sab4, {
        currentWalSalt0: state.currentWalSalt0,
        currentWalSalt1: state.currentWalSalt1,
        integratedFrameMax: state.integratedFrameMax,
    })
}
